'use client'
import type { StackProps } from '@chakra-ui/react'
import { VStack } from '@chakra-ui/react'
import { type DeliveryDTO, type Option, type ProductLightDTO, TagType } from 'ecosystem'
import type { FC, ReactNode } from 'react'
import { useMemo } from 'react'
import { getProductImages } from 'shared-utils/utils/products'
import { GrandHeading, GrandNextLink, GrandText, HoverableImages } from 'ui'
import type { ImageProps } from 'next/image'
import { type IHoverableImagesProps } from 'ui/common/HoverableImages'
import { useUIGlobalsContext } from 'ui/lib/ui-globals/hooks'
import ProductBadges from './ProductBadges'
import { useCartQuantities } from '../../cart/hooks/useCartQuantities'
import ProductListItemBuyOriginal, { type ProductListItemBuyProps } from './ProductListItemBuy'
import { QuantityDiscountTeaser } from './QuantityDiscountTeaser'

export type ProductListItemProps = StackProps & {
  onBuy: (item: ProductLightDTO) => void
  item: ProductLightDTO
  deliveryOptions: Option<DeliveryDTO[]>
  onProductRequest: (item: ProductLightDTO) => void
  productsUrlPath: string
  showsPrice: boolean
  imageProps?: Partial<ImageProps>
  imageAspectRatio?: IHoverableImagesProps['aspectRatio']
  subtitle?: ReactNode
  /**
   * Overrides the cart lookup that drives the volume-discount teaser. Only the
   * settings preview passes it — the admin has no cart slice, so the hook there
   * always reads empty and the card could never leave its first state.
   */
  cartQuantity?: number
  customComponents?: {
    ProductListItemBuy?: FC<ProductListItemBuyProps>
  }
}

const ProductListItemComponent: FC<ProductListItemProps> = ({
  onBuy,
  onProductRequest,
  item,
  deliveryOptions,
  productsUrlPath,
  showsPrice,
  imageProps,
  imageAspectRatio: _imageAspectRatio,
  subtitle,
  cartQuantity: cartQuantityOverride,
  customComponents,
  ...props
}) => {
  const { ProductListItemBuy = ProductListItemBuyOriginal } = customComponents ?? {}
  // What the shopper already holds of this product, bundled units included. The
  // map is memoised in the selector, so a full grid of cards walks the basket
  // once between them rather than once each.
  const cartQuantityFromStore = useCartQuantities().get(item.id) ?? 0
  const cartQuantity = cartQuantityOverride ?? cartQuantityFromStore
  const { productListItemImageAspectRatio, productListItemTitleLines } = useUIGlobalsContext()
  const { mainImageUrl, secondaryImageUrl } = getProductImages(item.medias)
  const productBadges = useMemo(
    () => item.tags?.filter((tag) => tag.type === TagType.BADGE) || [],
    [item.tags]
  )

  const imageAspectRatio = _imageAspectRatio ?? productListItemImageAspectRatio ?? 1

  return (
    <VStack
      _hover={{
        transform: {
          base: 'none',
          lg: 'translateY(-5px)'
        }
      }}
      bg="var(--chakra-colors-component-ProductListItem-root-background, var(--chakra-colors-background-surface))"
      borderRadius="sm"
      justifyContent="center"
      position="relative"
      pb={4}
      gap={1}
      transition="all 0.1s cubic-bezier(.17,.67,.83,.67)"
      {...props}
    >
      <GrandNextLink
        aria-label={item.name}
        h="full"
        href={`${productsUrlPath}/${item.slug}`}
        w="full"
      >
        <HoverableImages
          alt={item.name}
          badges={
            <ProductBadges
              badges={productBadges}
              imageProps={{
                sizes: `(min-width: 40em) 90px, 
                  (min-width: 30em) 80px,
                  60px
                `
              }}
              scalableSize={[60, 80, 90]}
            />
          }
          firstImgUrl={mainImageUrl}
          secondImgUrl={secondaryImageUrl || ''}
          title={item.name}
          imageProps={imageProps}
          aspectRatio={imageAspectRatio}
        />
      </GrandNextLink>

      <GrandHeading
        as="h3"
        fontSize={{
          base: 'sm',
          md: 'md'
        }}
        noOfLines={productListItemTitleLines ?? 1}
        pt={4}
        textAlign="center"
        title={item.name}
        tooltip={item.name}
        w="80%"
      />
      <GrandText
        fontSize={{
          base: 'xs',
          md: 'sm'
        }}
        py={3}
        textAlign="center"
        w="80%"
      >
        {subtitle ?? item.brand?.name}
      </GrandText>

      {/* Scored against what is already in the cart, not against zero: the
          shopper two units short of a tier is the one most worth nudging, and
          a card that ignored the cart would contradict the product page, which
          seeds its counter from the same basket. */}
      <QuantityDiscountTeaser
        px={4}
        product={item}
        quantity={cartQuantity}
        surface="listItem"
        textAlign="center"
        w="80%"
      />

      {showsPrice ? (
        <ProductListItemBuy
          price={{
            price: item.price,
            originalPrice: item.originalPrice,
            priceWithoutVat: item.priceWithoutVat,
            originalPriceWithoutVat: item.originalPriceWithoutVat
          }}
          product={item}
          {...{ deliveryOptions, onBuy, onProductRequest, productsUrlPath }}
        />
      ) : null}
    </VStack>
  )
}

export default ProductListItemComponent
